import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
  faRocket,
  faBell,
  faUserPlus,
  faCommentMedical,
  faRobot,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faTimes,
  faStar,
  faMousePointer,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../ui/BtnTaratic';
import api, { fastapi } from '../../services/api';

import { useDispatch } from 'react-redux';
import { closeChatUI } from '../../store/chat/chatSlice';

export interface OnboardingTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

interface TourStep {
  id: number;
  featureKey?: string;
  title: string;
  route: string;
  selector: string;
  icon: IconDefinition;
  actionText: string;
  description: string;
  highlights: string[];
  preferredPlacement?: 'right' | 'bottom' | 'top' | 'left';
  postActionSelector?: string;
}

import type { TFunction } from 'i18next';

const getTourSteps = (t: TFunction): TourStep[] => [
  {
    id: 2,
    featureKey: 'connections',
    title: t('onboardingTour.steps.connections.title'),
    route: '/settings/connections',
    selector: '[data-tour="connect-instagram"]',
    icon: faInstagram,
    preferredPlacement: 'left',
    actionText: t('onboardingTour.steps.connections.actionText'),
    description: t('onboardingTour.steps.connections.description'),
    highlights: [
      t('onboardingTour.steps.connections.highlight1'),
      t('onboardingTour.steps.connections.highlight2'),
    ],
  },
  {
    id: 1,
    title: t('onboardingTour.steps.notifications.title'),
    route: '/stats',
    selector: '[data-tour="notifications"]',
    icon: faBell,
    preferredPlacement: 'right',
    actionText: t('onboardingTour.steps.notifications.actionText'),
    description: t('onboardingTour.steps.notifications.description'),
    highlights: [
      t('onboardingTour.steps.notifications.highlight1'),
      t('onboardingTour.steps.notifications.highlight2'),
    ],
  },
  {
    id: 3,
    featureKey: 'clinics',
    title: t('onboardingTour.steps.patients.title'),
    route: '/clinics/patients',
    selector: '[data-tour="create-patient"]',
    icon: faUserPlus,
    preferredPlacement: 'left',
    actionText: t('onboardingTour.steps.patients.actionText'),
    description: t('onboardingTour.steps.patients.description'),
    highlights: [
      t('onboardingTour.steps.patients.highlight1'),
      t('onboardingTour.steps.patients.highlight2'),
    ],
  },
  {
    id: 4,
    featureKey: 'clinics',
    title: t('onboardingTour.steps.chat.title'),
    route: '/clinics/patients',
    selector: '[data-tour="patient-chat"]',
    icon: faCommentMedical,
    preferredPlacement: 'top',
    actionText: t('onboardingTour.steps.chat.actionText'),
    description: t('onboardingTour.steps.chat.description'),
    highlights: [
      t('onboardingTour.steps.chat.highlight1'),
      t('onboardingTour.steps.chat.highlight2'),
    ],
  },
  {
    id: 5,
    featureKey: 'chatbots',
    title: t('onboardingTour.steps.bots.title'),
    route: '/bots-config',
    selector: '[data-tour="bots-config"]',
    icon: faRobot,
    preferredPlacement: 'right',
    actionText: t('onboardingTour.steps.bots.actionText'),
    description: t('onboardingTour.steps.bots.description'),
    highlights: [
      t('onboardingTour.steps.bots.highlight1'),
      t('onboardingTour.steps.bots.highlight2'),
    ],
  },
];

export const OnboardingTourModal: React.FC<OnboardingTourModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [phase, setPhase] = useState<'welcome' | 'tour'>('welcome');
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | null>(null);
  const [completedStepIds, setCompletedStepIds] = useState<Set<number>>(new Set());
  const [preCompletedStepIds, setPreCompletedStepIds] = useState<Set<number>>(new Set());
  const [searchingTarget, setSearchingTarget] = useState<boolean>(false);

  // Fetch tenant features and pre-completed configurations
  useEffect(() => {
    if (!isOpen) return;

    const fetchFeaturesAndStatus = async () => {
      try {
        const [featuresRes, igRes, botsRes, patientsRes] = await Promise.allSettled([
          fastapi.get('/tenants/me/features'),
          api.get('/instagram/connection-status'),
          api.get('/v1/chat-bots'),
          fastapi.get('/clinics/patients', { params: { page: 1, per_page: 1 } })
        ]);

        if (featuresRes.status === 'fulfilled' && Array.isArray(featuresRes.value.data)) {
          setEnabledFeatures(featuresRes.value.data);
        } else {
          setEnabledFeatures(null);
        }

        const doneIds = new Set<number>();
        
        // Instagram Step
        if (igRes.status === 'fulfilled' && igRes.value.data?.connected) {
          doneIds.add(2);
        }
        
        // Patients Step
        if (
          patientsRes.status === 'fulfilled' &&
          (patientsRes.value.data?.patients?.length > 0 ||
            patientsRes.value.data?.total > 0 ||
            patientsRes.value.data?.items?.length > 0 ||
            patientsRes.value.data?.length > 0)
        ) {
          doneIds.add(3); // Create patient
          // Step 4 (Chat Patient) remains pending to encourage starting a conversation, 
          // or we could mark it done if they have >0 patients. Let's keep it pending for interactivity.
        }
        
        // Chatbots Step
        if (botsRes.status === 'fulfilled' && (botsRes.value.data?.data?.length > 0 || botsRes.value.data?.length > 0)) {
          doneIds.add(5);
        }
        
        setPreCompletedStepIds(doneIds);

      } catch (e) {
        console.warn('Could not fetch onboarding pre-status:', e);
        setEnabledFeatures(null);
      }
    };

    fetchFeaturesAndStatus();
  }, [isOpen]);

  // Filter steps based on enabled features (or if featureKey is absent)
  const activeSteps = useMemo(() => {
    if (!enabledFeatures) return []; // Wait for feature flags
    return getTourSteps(t).filter(step =>
      !step.featureKey || enabledFeatures.includes(step.featureKey)
    ).sort((a, b) => a.id - b.id);
  }, [enabledFeatures, t]);

  const currentStep = activeSteps[currentStepIndex] || activeSteps[0];

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setPhase('welcome');
      setCurrentStepIndex(0);
      setCompletedStepIds(new Set());
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // AUTOMATIC NAVIGATION & TARGET FINDER
  useEffect(() => {
    if (!isOpen || phase !== 'tour' || !currentStep) return;

    // 1. Force navigate if not exactly on target route
    if (location.pathname !== currentStep.route) {
      navigate(currentStep.route);
    }

    setSearchingTarget(true);

    let resizeObserver: ResizeObserver | null = null;
    let attempts = 0;
    const findAndPositionTarget = () => {
      attempts++;
      const activeSelector = currentStep.selector;
      const el = document.querySelector(activeSelector);
      if (el) {
        // Bring element cleanly into view
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        } catch {}

        setTargetRect(el.getBoundingClientRect());
        setSearchingTarget(false);
        
        // Observe changes in element's size
        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            const currentEl = document.querySelector(currentStep.selector);
            if (currentEl) {
              setTargetRect(currentEl.getBoundingClientRect());
            }
          });
          resizeObserver.observe(el);
        }
      } else if (attempts < 12) {
        setTimeout(findAndPositionTarget, 150);
      } else {
        setTargetRect(null);
        setSearchingTarget(false);
      }
    };

    const timer = setTimeout(findAndPositionTarget, 200);

    const handleUpdate = () => {
      const activeSelector = currentStep.selector;
      const el = document.querySelector(activeSelector);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      }
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      clearTimeout(timer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isOpen, phase, currentStepIndex, currentStep, location.pathname, navigate]);

  if (!isOpen || !currentStep) return null;

  const markCompletedInBackend = async () => {
    try {
      await fastapi.patch('/auth/onboarding-tour', { completed: true, enabled: false });
    } catch (err) {
      console.warn('Could not update onboarding status in backend:', err);
    }
  };

  const handleStartTour = () => {
    setPhase('tour');
    // Find the first step that is not pre-completed
    let startIndex = 0;
    for (let i = 0; i < activeSteps.length; i++) {
      if (!preCompletedStepIds.has(activeSteps[i].id)) {
        startIndex = i;
        break;
      }
    }
    setCurrentStepIndex(startIndex);
    const firstStep = activeSteps[startIndex];
    if (firstStep && location.pathname !== firstStep.route) {
      navigate(firstStep.route);
    }
  };

  const handleSkip = async () => {
    await markCompletedInBackend();
    onClose();
  };

  const handleNext = async () => {
    if (currentStep.id === 1) {
      dispatch(closeChatUI());
    }

    setCompletedStepIds((prev) => new Set(prev).add(currentStep.id));

    if (currentStepIndex < activeSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      const nextStep = activeSteps[nextIndex];
      if (nextStep && location.pathname !== nextStep.route) {
        navigate(nextStep.route);
      }
    } else {
      await markCompletedInBackend();
      if (onComplete) onComplete();
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      const prevStep = activeSteps[prevIndex];
      if (prevStep && location.pathname !== prevStep.route) {
        navigate(prevStep.route);
      }
    } else {
      setPhase('welcome');
    }
  };

  // DYNAMIC TOOLTIP POSITION & POINTER ARROW PLACEMENT
  const placementInfo = (() => {
    const cardWidth = Math.min(390, window.innerWidth - 32);
    const cardHeight = 290;

    if (!targetRect) {
      return {
        style: {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${cardWidth}px`,
        } as React.CSSProperties,
        arrowClass: '',
      };
    }

    const preferred = currentStep.preferredPlacement || 'bottom';
    let top = 0;
    let left = 0;
    let arrowClass = '';

    if (preferred === 'right') {
      left = targetRect.right + 16;
      top = targetRect.top - 10;
      arrowClass = '-left-2 top-6 border-b border-l';
      if (left + cardWidth > window.innerWidth - 16) {
        left = Math.max(16, targetRect.left - cardWidth - 16);
        arrowClass = '-right-2 top-6 border-t border-r';
      }
    } else if (preferred === 'left') {
      left = targetRect.left - cardWidth - 16;
      top = targetRect.top - 10;
      arrowClass = '-right-2 top-6 border-t border-r';
      if (left < 16) {
        left = targetRect.right + 16;
        arrowClass = '-left-2 top-6 border-b border-l';
      }
    } else if (preferred === 'top') {
      top = targetRect.top - cardHeight - 16;
      left = targetRect.left;
      arrowClass = '-bottom-2 left-8 border-b border-r';
      if (top < 16) {
        top = targetRect.bottom + 16;
        arrowClass = '-top-2 left-8 border-t border-l';
      }
    } else {
      // default bottom
      top = targetRect.bottom + 16;
      left = targetRect.left;
      arrowClass = '-top-2 left-8 border-t border-l';
      if (top + cardHeight > window.innerHeight - 16) {
        top = Math.max(16, targetRect.top - cardHeight - 16);
        arrowClass = '-bottom-2 left-8 border-b border-r';
      }
    }

    // Clamp inside viewport
    left = Math.max(16, Math.min(window.innerWidth - cardWidth - 16, left));
    top = Math.max(16, Math.min(window.innerHeight - cardHeight - 16, top));

    return {
      style: {
        top: `${top}px`,
        left: `${left}px`,
        width: `${cardWidth}px`,
      } as React.CSSProperties,
      arrowClass,
    };
  })();

  const isCurrentStepDone = completedStepIds.has(currentStep.id) || preCompletedStepIds.has(currentStep.id);

  return (
    <div className="fixed inset-0 z-[99999] pointer-events-none">
      {/* Semi-transparent Backdrop Curtain - Blocks all clicks during tour to make it purely informative */}
      <div
        className={`fixed inset-0 transition-opacity duration-300 z-[99998] pointer-events-auto ${
          phase === 'welcome' 
            ? 'bg-black/60 backdrop-filter backdrop-blur-[2px]' 
            : ''
        }`}
        onClick={phase === 'welcome' ? handleSkip : undefined}
      />

      {/* Target Focus Ring Overlay */}
      {phase === 'tour' && targetRect && (
        <div
          className={`fixed pointer-events-none z-[99999] transition-all duration-300 rounded-xl border-2 ${
            isCurrentStepDone
              ? 'border-green-400 shadow-[0_0_30px_rgba(74,222,128,0.9)]'
              : 'border-blue-400/90 shadow-[0_0_25px_rgba(59,130,246,0.9)] animate-pulse'
          }`}
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
          }}
        />
      )}

      {/* Phase 1: Welcome Modal */}
      {phase === 'welcome' && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 pointer-events-auto">
          <div className="relative w-full max-w-xl bg-black/80 backdrop-filter backdrop-blur-2xl rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/20 text-white space-y-6 animate-fade-in-up z-[99999]">
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              title={t('onboardingTour.welcome.close')}
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600/30 to-indigo-600/30 border border-blue-500/30 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <FontAwesomeIcon icon={faRocket} className="text-blue-400 text-2xl icon-inner-glow-white animate-bounce" />
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-semibold uppercase tracking-wider mb-1">
                  <FontAwesomeIcon icon={faStar} className="text-xs text-yellow-400" />
                  <span>{t('onboardingTour.welcome.badge')}</span>
                </div>
                <h2 className="text-2xl font-black text-white">{t('onboardingTour.welcome.title')}</h2>
              </div>
            </div>

            <p className="text-white/70 text-sm leading-relaxed">
              {t('onboardingTour.welcome.description')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
              {activeSteps.map((step, idx) => {
                const isPreDone = preCompletedStepIds.has(step.id);
                return (
                  <div key={step.id} className={`flex items-center gap-2.5 p-2.5 rounded-xl border ${isPreDone ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-white/5 border-white/5 text-white/80'}`}>
                    <FontAwesomeIcon icon={isPreDone ? faCheckCircle : step.icon} className={`${isPreDone ? 'text-green-400' : 'text-blue-400'} text-sm icon-inner-glow-white shrink-0`} />
                    <span className="font-medium truncate">{idx + 1}. {step.title}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-white/10">
              <button
                onClick={handleSkip}
                className="text-xs text-white/40 hover:text-white/80 transition-colors font-medium hover:underline"
              >
                {t('onboardingTour.welcome.skip')}
              </button>
              <BtnTaratic
                color="blue"
                onClick={handleStartTour}
                className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30"
              >
                <span>{t('onboardingTour.welcome.start')}</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </BtnTaratic>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2: Action-Anchored Dynamic Tooltip Modal */}
      {phase === 'tour' && (
        <div
          className="fixed z-[100000] pointer-events-auto transition-all duration-300 ease-in-out animate-fade-in-up"
          style={placementInfo.style}
        >
          {/* Caret pointing arrow */}
          {targetRect && placementInfo.arrowClass && (
            <div className={`absolute w-3.5 h-3.5 bg-black/80 backdrop-blur-xl border-white/20 rotate-45 z-[99999] ${placementInfo.arrowClass}`} />
          )}

          <div className="bg-black/80 backdrop-blur-xl border border-white/20 p-5 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)] flex flex-col gap-3 relative pointer-events-auto">
            {/* Header Badge & Action status */}
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-blue-600/40 to-purple-600/40 border border-blue-400/40 text-blue-300 text-[11px] font-bold font-mono">
                <FontAwesomeIcon icon={faMousePointer} className="text-yellow-400 text-xs animate-bounce" />
                <span>{t('onboardingTour.tour.step')} {currentStepIndex + 1} {t('onboardingTour.tour.of')} {activeSteps.length}</span>
              </div>

              {searchingTarget ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-300">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                  <span>{t('onboardingTour.tour.loading')}</span>
                </span>
              ) : null}

              <button
                onClick={handleSkip}
                className="text-white/40 hover:text-white transition-colors p-1 rounded hover:bg-white/10 text-xs"
                title={t('onboardingTour.tour.skip')}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Step Title & Instruction */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={currentStep.icon} className="text-blue-400 text-lg icon-inner-glow-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white leading-snug">{currentStep.title}</h3>
                <p className="text-xs text-blue-300 font-semibold mt-0.5 flex items-center gap-1">
                  <FontAwesomeIcon icon={faMousePointer} className="text-[10px]" />
                  <span>{currentStep.actionText}</span>
                </p>
              </div>
            </div>

            <p className="text-xs text-white/70 leading-relaxed">{currentStep.description}</p>

            {/* Checklist items with animated verification */}
            <div className="space-y-1.5 pt-0.5">
              {currentStep.highlights.map((h, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 text-[11px] p-1.5 rounded-lg transition-all duration-300 border ${
                    isCurrentStepDone
                      ? 'bg-green-500/10 border-green-500/30 text-green-200'
                      : 'bg-white/5 border-white/5 text-white/80'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className={`text-xs shrink-0 transition-transform duration-300 ${
                      isCurrentStepDone ? 'text-green-400 scale-125 icon-inner-glow-white' : 'text-white/30'
                    }`}
                  />
                  <span className="font-medium">{h}</span>
                </div>
              ))}
            </div>

            {/* Clean Non-Clickable Visual Progress Indicators */}
            <div className="flex items-center justify-center gap-2 py-1">
              {activeSteps.map((s, idx) => {
                const isDone = completedStepIds.has(s.id) || preCompletedStepIds.has(s.id) || idx < currentStepIndex;
                return (
                  <div
                    key={s.id}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === currentStepIndex
                        ? 'w-6 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]'
                        : isDone
                        ? 'w-2 bg-green-500 shadow-[0_0_6px_rgba(74,222,128,0.8)]'
                        : 'w-2 bg-white/20'
                    }`}
                  />
                );
              })}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <BtnTaratic
                color="ghost-dark"
                onClick={handlePrev}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                <span>{t('onboardingTour.tour.prev')}</span>
              </BtnTaratic>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSkip}
                  className="text-xs text-white/40 hover:text-white/80 transition-colors px-2 py-1"
                >
                  {t('onboardingTour.tour.skipShort')}
                </button>
                <BtnTaratic
                  color="blue"
                  onClick={handleNext}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold shadow-md shadow-blue-600/30"
                >
                  <span>{currentStepIndex === activeSteps.length - 1 ? t('onboardingTour.tour.finish') : t('onboardingTour.tour.next')}</span>
                  <FontAwesomeIcon icon={currentStepIndex === activeSteps.length - 1 ? faCheckCircle : faChevronRight} />
                </BtnTaratic>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingTourModal;
